import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { CoachingSessionCard } from "@/components/cards/CoachingSessionCard"
import { CommunityCard } from "@/components/cards/communityCard"
import { Breadcrumb } from "@/components/shared/Breadcrumb"
import { UserCard } from "@/features/catalog/components/UserCard"
import { ExpertCard } from "@/features/connect/components/ExpertCard"
import { useFollowActions } from "@/features/profile/hooks/useFollowActions"
import { toggleSavedRitual } from "@/features/profile/services/saved-rituals.service"
import { getSpiritualTraditions } from "@/features/profile/services/user.service"
import { useTranslation } from "@/language"
import { IntentionFilters } from "../components/IntentionFilters"
import { IntentionHeader } from "../components/IntentionHeader"
import { IntentionRitualGrid } from "../components/IntentionRitualGrid"
import { IntentionSearchBar } from "../components/IntentionSearchBar"
import { useIntention } from "../hooks/useIntention"
import { useIntentionRituals } from "../hooks/useIntentionRituals"
import { useToggleIntention } from "../hooks/useToggleIntention"
import {
  getIntentionCommunities,
  getIntentionExperts,
  getIntentionMembers,
  getIntentionRitualists,
  getIntentionSessions,
} from "../services/intention.service"
import type {
  IntentionCommunity,
  IntentionFiltersState,
  IntentionSession,
  IntentionTab,
  IntentionUser,
} from "../types"
import { INTENTION_TABS } from "../types"

const DEFAULT_FILTERS: IntentionFiltersState = {
  search: "",
  sort: "trending",
  price: "all",
  format: "all",
  duration: "all",
}

function usePaginatedList<T>(
  key: string,
  fetcher: (
    page: number,
  ) => Promise<{ total: number; page: number; totalPages: number; items: T[] }>,
) {
  const [items, setItems] = useState<T[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)

  const fetcherRef = useRef(fetcher)
  useEffect(() => {
    fetcherRef.current = fetcher
  }, [fetcher])

  // biome-ignore lint/correctness/useExhaustiveDependencies: key triggers reset + refetch on tab/filter change
  useEffect(() => {
    setItems([])
    setPage(1)
    setTotalPages(1)
    // Fetch fresh data when key changes
    let cancelled = false
    setIsLoading(true)
    fetcherRef
      .current(1)
      .then(result => {
        if (cancelled) return
        setItems(result.items)
        setTotalPages(result.totalPages)
        setPage(1)
        setIsLoading(false)
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [key])

  const fetch = useCallback(async (pageNum: number, append = false) => {
    if (append) setIsFetchingMore(true)
    else setIsLoading(true)
    try {
      const result = await fetcherRef.current(pageNum)
      setItems(prev => (append ? [...prev, ...result.items] : result.items))
      setTotalPages(result.totalPages)
      setPage(result.page)
    } finally {
      setIsLoading(false)
      setIsFetchingMore(false)
    }
  }, [])

  const triggerFetch = useCallback(() => {
    fetch(1)
  }, [fetch])

  const loadMore = useCallback(async () => {
    if (page >= totalPages) return
    fetch(page + 1, true)
  }, [page, totalPages, fetch])

  return {
    items,
    isLoading,
    isFetchingMore,
    hasMore: page < totalPages,
    loadMore,
    refetch: triggerFetch,
  }
}

function mapToCardUser(u: IntentionUser) {
  return {
    id: u.id,
    name: u.name ?? "",
    username: u.username ?? "",
    profilePictureUrl: u.profilePictureUrl,
    followerCount: u.followerCount ?? 0,
    city: u.city,
    state: u.state,
    bio: u.bio,
    tags: u.tags ?? [],
    isFollowing: u.isFollowing,
  }
}

function mapToCardExpert(u: IntentionUser) {
  return {
    id: u.id,
    name: u.name ?? "",
    username: u.username ?? "",
    profilePictureUrl: u.profilePictureUrl,
    followerCount: u.followerCount ?? 0,
    city: u.city,
    state: u.state,
    bio: u.bio,
    tags: u.tags ?? [],
    isFollowing: u.isFollowing,
    sessionsAvailable: u.sessionCount ?? u.metricCount ?? 0,
  }
}

function EmptyState({ label }: { label: string }) {
  const { t } = useTranslation("learnPractice")
  return (
    <div className="flex flex-col items-center justify-center py-20 text-primary/60">
      <p className="text-lg">{t("detail.emptyLabel", { label })}</p>
      <p className="text-sm mt-2">{t("detail.tryAdjusting")}</p>
    </div>
  )
}

export function IntentionDetailScreen() {
  const { intentionId: slug } = useParams<{ intentionId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get("tab") as IntentionTab) || "rituals"
  const { t } = useTranslation("learnPractice")

  const tabLabelMap: Record<IntentionTab, string> = {
    rituals: t("tabs.rituals"),
    sessions: t("tabs.sessions"),
    community: t("tabs.community"),
    members: t("tabs.members"),
    experts: t("tabs.experts"),
    ritualists: t("tabs.ritualists"),
  }

  const [ritualFilters, setRitualFilters] = useState<IntentionFiltersState>(DEFAULT_FILTERS)
  const [tabFilters, setTabFilters] = useState<IntentionFiltersState>(DEFAULT_FILTERS)

  const {
    rituals,
    hasNextPage: ritualsHasMore,
    isLoading: ritualsLoading,
    isFetchingNextPage: ritualsFetchingMore,
    fetchNextPage: ritualsLoadMore,
  } = useIntentionRituals(slug ?? "", ritualFilters)

  const sessions = usePaginatedList<IntentionSession>(
    `sessions-${tabFilters.search}-${tabFilters.search}-${tabFilters.sort}-${tabFilters.traditions}-${tabFilters.duration}`,
    page =>
      getIntentionSessions(
        slug ?? "",
        {
          sort: tabFilters.sort,
          search: tabFilters.search,
          spiritualTradition: tabFilters.traditions ?? "",
          duration: tabFilters.duration,
        },
        page,
      ).then(r => ({ ...r, items: r.sessions })),
  )
  const communities = usePaginatedList<IntentionCommunity>(
    `community-${tabFilters.search}-${tabFilters.sort}-${tabFilters.traditions}`,
    page =>
      getIntentionCommunities(
        slug ?? "",
        {
          sort: tabFilters.sort,
          search: tabFilters.search,
          spiritualTradition: tabFilters.traditions ?? "",
        },
        page,
      ).then(r => ({ ...r, items: r.communities })),
  )
  const members = usePaginatedList<IntentionUser>(
    `members-${tabFilters.search}-${tabFilters.sort}-${tabFilters.traditions}`,
    page =>
      getIntentionMembers(
        slug ?? "",
        {
          sort: tabFilters.sort,
          search: tabFilters.search,
          spiritualTradition: tabFilters.traditions ?? "",
        },
        page,
      ).then(r => ({ ...r, items: r.members ?? [] })),
  )
  const experts = usePaginatedList<IntentionUser>(
    `experts-${tabFilters.search}-${tabFilters.sort}-${tabFilters.traditions}`,
    page =>
      getIntentionExperts(
        slug ?? "",
        {
          sort: tabFilters.sort,
          search: tabFilters.search,
          spiritualTradition: tabFilters.traditions ?? "",
        },
        page,
      ).then(r => ({ ...r, items: r.experts ?? [] })),
  )
  const ritualists = usePaginatedList<IntentionUser>(
    `ritualists-${tabFilters.search}-${tabFilters.sort}-${tabFilters.traditions}`,
    page =>
      getIntentionRitualists(
        slug ?? "",
        {
          sort: tabFilters.sort,
          search: tabFilters.search,
          spiritualTradition: tabFilters.traditions ?? "",
        },
        page,
      ).then(r => ({ ...r, items: r.ritualists ?? [] })),
  )

  const { data: traditionsList = [] } = useQuery({
    queryKey: ["intention-detail-traditions"],
    queryFn: getSpiritualTraditions,
    staleTime: 5 * 60 * 1000,
  })

  const { follow, unfollow } = useFollowActions()

  const handleFollow = useCallback(
    (userId: string) => {
      follow(userId)
      // Trigger refetch of current tab after follow
      if (activeTab === "members") members.refetch()
      else if (activeTab === "experts") experts.refetch()
      else if (activeTab === "ritualists") ritualists.refetch()
    },
    [follow, activeTab, members.refetch, experts.refetch, ritualists.refetch],
  )

  const handleUnfollow = useCallback(
    (userId: string) => {
      unfollow(userId)
      if (activeTab === "members") members.refetch()
      else if (activeTab === "experts") experts.refetch()
      else if (activeTab === "ritualists") ritualists.refetch()
    },
    [unfollow, activeTab, members.refetch, experts.refetch, ritualists.refetch],
  )

  const { intention, isLoading: intentionLoading } = useIntention(slug ?? "")
  const toggleMutation = useToggleIntention(slug ?? "")
  const queryClient = useQueryClient()
  const bookmarkMutation = useMutation({
    mutationFn: (ritualId: string) => toggleSavedRitual(ritualId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["intentionRituals", slug] })
      queryClient.invalidateQueries({ queryKey: ["saved-rituals"] })
    },
  })

  const handleTabChange = useCallback(
    (tab: IntentionTab) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          if (tab === "rituals") next.delete("tab")
          else next.set("tab", tab)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const handleToggleIntention = useCallback(() => {
    if (intention) toggleMutation.mutate(intention.id)
  }, [intention, toggleMutation])

  // Trigger fetch when tab becomes active
  useEffect(() => {
    switch (activeTab) {
      case "sessions":
        sessions.refetch()
        break
      case "community":
        communities.refetch()
        break
      case "members":
        members.refetch()
        break
      case "experts":
        experts.refetch()
        break
      case "ritualists":
        ritualists.refetch()
        break
    }
  }, [
    activeTab,
    sessions.refetch,
    communities.refetch,
    members.refetch,
    experts.refetch,
    ritualists.refetch,
  ])

  if (intentionLoading) {
    return (
      <div className="bg-background min-h-screen">
        <div className="py-6">
          <div className="h-4 w-48 bg-white/10 rounded animate-pulse" />
          <div className="h-10 w-64 bg-white/10 rounded animate-pulse mt-4" />
          <div className="h-4 w-96 bg-white/10 rounded animate-pulse mt-3" />
        </div>
      </div>
    )
  }

  if (!intention) {
    return (
      <div className="bg-background min-h-screen flex items-center justify-center">
        <p className="text-white/60 text-lg">{t("detail.notFound")}</p>
      </div>
    )
  }

  const breadcrumbItems = [
    { label: t("detail.breadcrumbHome"), href: "/" },
    { label: t("detail.breadcrumbLearn"), href: "/learn-and-practice" },
    { label: intention.name },
  ]

  return (
    <div className="min-h-screen max-w-360 mx-auto px-5 md:px-17.5">
      <Breadcrumb items={breadcrumbItems} className="pt-5 pb-[10px] hidden md:flex" />
      <div className="mb-[14px]">
        <IntentionHeader
          intention={intention}
          isToggling={toggleMutation.isPending}
          onToggle={handleToggleIntention}
        />
      </div>
      <div className="mb-[20px]">
        <IntentionSearchBar
          className="!mt-0 !pt-0"
          onSearchChange={query => {
            setRitualFilters(prev => ({ ...prev, search: query }))
            setTabFilters(prev => ({ ...prev, search: query }))
          }}
          onLocationChange={(city, state, country) => {
            setRitualFilters(prev => ({ ...prev, city, state, country }))
          }}
        />
      </div>
      <div className="flex items-center w-full">
        <div className="overflow-x-auto overflow-y-hidden -mx-5 px-5 lg:mx-0 lg:px-0 scrollbar-hide flex-1">
          <div className="flex flex-nowrap lg:flex-wrap">
            {INTENTION_TABS.map(tab => {
              const isActive = tab.key === activeTab
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => handleTabChange(tab.key)}
                  className={`px-5 py-5 text-[15px] font-semibold uppercase whitespace-nowrap border-b transition-colors shrink-0 ${isActive ? "text-text-primary border-text-primary" : "text-text-primary/50 border-white/20 hover:text-text-primary/70"}`}
                >
                  {tabLabelMap[tab.key]}
                </button>
              )
            })}
            <div className="hidden lg:block flex-1 border-b border-white/20 h-[65px]" />
          </div>
        </div>
      </div>

      {/* Rituals */}
      {activeTab === "rituals" && (
        <>
          <IntentionFilters
            filters={ritualFilters}
            intentionName={intention.name}
            onChange={setRitualFilters}
            traditionsList={traditionsList}
            showPrice
            showFormat
            showContent
            showSpiritualTraditions
            showDuration
          />
          <p className="text-[14px] leading-[25px] text-primary/90 mt-[20px] mb-[30px]">
            {rituals.length === 1
              ? t("detail.resultsCount", { count: rituals.length, name: intention.name })
              : t("detail.resultsCount_plural", { count: rituals.length, name: intention.name })}
          </p>
          <IntentionRitualGrid
            rituals={rituals}
            isLoading={ritualsLoading || ritualsFetchingMore}
            hasMore={ritualsHasMore}
            onLoadMore={ritualsLoadMore}
            onBookmark={id => bookmarkMutation.mutate(id)}
          />
        </>
      )}

      {/* Sessions */}
      {activeTab === "sessions" && (
        <>
          <IntentionFilters
            filters={tabFilters}
            intentionName={intention.name}
            onChange={setTabFilters}
            traditionsList={traditionsList}
            showSpiritualTraditions
            showDuration
          />
          <p className="text-[14px] leading-[25px] text-primary/90 mt-[20px] mb-[30px]">
            {sessions.items.length === 1
              ? t("detail.resultsCount", { count: sessions.items.length, name: intention.name })
              : t("detail.resultsCount_plural", {
                  count: sessions.items.length,
                  name: intention.name,
                })}
          </p>
          {sessions.isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[440px] rounded-tl-[40px] rounded-br-[40px] bg-card animate-pulse"
                />
              ))}
            </div>
          ) : sessions.items.length === 0 ? (
            <EmptyState label={t("tabs.sessions")} />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                {sessions.items.map(s => (
                  <CoachingSessionCard
                    key={s.id}
                    id={s.id}
                    title={s.title}
                    coverImage={s.coverImage}
                    format={s.format}
                    duration={s.duration}
                    description={(s as { description?: string | null }).description}
                    participationFee={s.participationFee}
                    price={s.price}
                    creator={
                      s.creator
                        ? {
                            id: s.creator.id,
                            name: s.creator.name,
                            username: s.creator.username,
                            profilePictureUrl: s.creator.profilePictureUrl ?? undefined,
                            followers: s.creator.followers,
                          }
                        : undefined
                    }
                    tags={s.tags?.map(t => t.tagText)}
                  />
                ))}
              </div>
              {sessions.hasMore && (
                <div className="flex justify-center mt-10 mb-[90px]">
                  <button
                    type="button"
                    onClick={sessions.loadMore}
                    disabled={sessions.isFetchingMore}
                    className="h-[50px] w-[280px] rounded-full border border-primary text-primary text-sm font-semibold uppercase hover:bg-primary/5 transition-colors"
                  >
                    {sessions.isFetchingMore ? t("detail.loading") : t("detail.loadMore")}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Community */}
      {activeTab === "community" && (
        <>
          <IntentionFilters
            filters={tabFilters}
            intentionName={intention.name}
            onChange={setTabFilters}
            traditionsList={traditionsList}
            showSpiritualTraditions
          />
          <p className="text-[14px] leading-[25px] text-primary/90 mt-[20px] mb-[30px]">
            {communities.items.length === 1
              ? t("detail.resultsCount", { count: communities.items.length, name: intention.name })
              : t("detail.resultsCount_plural", {
                  count: communities.items.length,
                  name: intention.name,
                })}
          </p>
          {communities.isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-[300px] rounded-[20px] bg-card animate-pulse" />
              ))}
            </div>
          ) : communities.items.length === 0 ? (
            <EmptyState label={t("tabs.community")} />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                {communities.items.map(c => (
                  <CommunityCard
                    key={c.id}
                    community={c as unknown as Parameters<typeof CommunityCard>[0]["community"]}
                  />
                ))}
              </div>
              {communities.hasMore && (
                <div className="flex justify-center mt-10 mb-[90px]">
                  <button
                    type="button"
                    onClick={communities.loadMore}
                    disabled={communities.isFetchingMore}
                    className="h-[50px] w-[280px] rounded-full border border-primary text-primary text-sm font-semibold uppercase hover:bg-primary/5 transition-colors"
                  >
                    {communities.isFetchingMore ? t("detail.loading") : t("detail.loadMore")}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Members */}
      {activeTab === "members" && (
        <>
          <IntentionFilters
            filters={tabFilters}
            intentionName={intention.name}
            onChange={setTabFilters}
            traditionsList={traditionsList}
            showSpiritualTraditions
          />
          <p className="text-[14px] leading-[25px] text-primary/90 mt-[20px] mb-[30px]">
            {members.items.length === 1
              ? t("detail.resultsCountSimple", { count: members.items.length })
              : t("detail.resultsCountSimple_plural", { count: members.items.length })}
          </p>
          {members.isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-20 rounded-[20px] bg-card animate-pulse" />
              ))}
            </div>
          ) : members.items.length === 0 ? (
            <EmptyState label={t("tabs.members")} />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                {members.items.map(u => (
                  <UserCard
                    key={u.id}
                    user={mapToCardUser(u)}
                    onFollow={handleFollow}
                    onUnfollow={handleUnfollow}
                  />
                ))}
              </div>
              {members.hasMore && (
                <div className="flex justify-center mt-10 mb-[90px]">
                  <button
                    type="button"
                    onClick={members.loadMore}
                    disabled={members.isFetchingMore}
                    className="h-[50px] w-[280px] rounded-full border border-primary text-primary text-sm font-semibold uppercase hover:bg-primary/5 transition-colors"
                  >
                    {members.isFetchingMore ? t("detail.loading") : t("detail.loadMore")}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Experts */}
      {activeTab === "experts" && (
        <>
          <IntentionFilters
            filters={tabFilters}
            intentionName={intention.name}
            onChange={setTabFilters}
            traditionsList={traditionsList}
            showSpiritualTraditions
          />
          <p className="text-[14px] leading-[25px] text-primary/90 mt-[20px] mb-[30px]">
            {experts.items.length === 1
              ? t("detail.resultsCountSimple", { count: experts.items.length })
              : t("detail.resultsCountSimple_plural", { count: experts.items.length })}
          </p>
          {experts.isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-20 rounded-[20px] bg-card animate-pulse" />
              ))}
            </div>
          ) : experts.items.length === 0 ? (
            <EmptyState label={t("tabs.experts")} />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                {experts.items.map(u => (
                  <ExpertCard
                    key={u.id}
                    expert={mapToCardExpert(u)}
                    onFollow={handleFollow}
                    onUnfollow={handleUnfollow}
                  />
                ))}
              </div>
              {experts.hasMore && (
                <div className="flex justify-center mt-10 mb-[90px]">
                  <button
                    type="button"
                    onClick={experts.loadMore}
                    disabled={experts.isFetchingMore}
                    className="h-[50px] w-[280px] rounded-full border border-primary text-primary text-sm font-semibold uppercase hover:bg-primary/5 transition-colors"
                  >
                    {experts.isFetchingMore ? t("detail.loading") : t("detail.loadMore")}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Ritualists */}
      {activeTab === "ritualists" && (
        <>
          <IntentionFilters
            filters={tabFilters}
            intentionName={intention.name}
            onChange={setTabFilters}
            traditionsList={traditionsList}
            showSpiritualTraditions
          />
          <p className="text-[14px] leading-[25px] text-primary/90 mt-[20px] mb-[30px]">
            {ritualists.items.length === 1
              ? t("detail.resultsCountSimple", { count: ritualists.items.length })
              : t("detail.resultsCountSimple_plural", { count: ritualists.items.length })}
          </p>
          {ritualists.isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-20 rounded-[20px] bg-card animate-pulse" />
              ))}
            </div>
          ) : ritualists.items.length === 0 ? (
            <EmptyState label={t("tabs.ritualists")} />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                {ritualists.items.map(u => (
                  <UserCard
                    key={u.id}
                    user={mapToCardUser(u)}
                    onFollow={handleFollow}
                    onUnfollow={handleUnfollow}
                  />
                ))}
              </div>
              {ritualists.hasMore && (
                <div className="flex justify-center mt-10 mb-[90px]">
                  <button
                    type="button"
                    onClick={ritualists.loadMore}
                    disabled={ritualists.isFetchingMore}
                    className="h-[50px] w-[280px] rounded-full border border-primary text-primary text-sm font-semibold uppercase hover:bg-primary/5 transition-colors"
                  >
                    {ritualists.isFetchingMore ? t("detail.loading") : t("detail.loadMore")}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
